import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import {
    MessageContent,
    MessageInput,
    isMessageDocEmpty,
    legacyTextToMessageDoc,
    mentionCandidates,
    stringifyMessageDoc,
} from "components/atoms/message";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button";
import Checkbox from "components/atoms/input/Checkbox";
import Text from "components/atoms/text/Text";

import ReferenceReviewThreadList from "components/organisms/reference/ReferenceReviewThreadList";
import ReferenceReviewMessage from "components/organisms/reference/ReferenceReviewMessage";

import Constants from "utils/constants";
import { isReadonlyUser } from "utils/user";

const Container = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    border-left: 0;
    background-color: var(--color-White);
    overflow: hidden;

    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    width: 100%;
    min-height: 44px;
    padding: 0 12px 0 16px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    background-color: var(--color-White);
    border-bottom: solid 1px #edf1f4;
    overflow: hidden;
    transition: all 120ms ease-in;
`;

const HeaderMain = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: 0;
    gap: 8px;
`;

const HeaderTitleGroup = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    gap: 6px;
`;

const HeaderButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    background-color: transparent;
    border-radius: 6px;
    border: transparent;
    outline: none;
    cursor: pointer;
    transition: background-color 120ms ease-in;

    &:hover {
        background-color: var(--color-Base1);
    }
`;

const HeaderActionButton = styled.button`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background-color: transparent;
    border-radius: 6px;
    border: 0;
    outline: none;
    cursor: pointer;
    transition: background-color 120ms ease-in;

    &:hover {
        background-color: var(--color-Base1);
    }
`;

const HeaderTitle = styled(Text).attrs({
    variant: "body-md-500",
    color: "var(--color-SubBlack)",
})`
    flex: 1;
    min-width: 0;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const HeaderTitleInput = styled(MessageInput)`
    flex: 1;
    min-width: 0;
    border-radius: 4px;
    box-shadow: none;

    .ProseMirror {
        min-height: 24px;
        max-height: 24px;
        padding: 0;
        overflow: hidden;
        white-space: nowrap;
        font-size: 1.3rem;
        line-height: 24px;
    }
`;

const HeaderContextMenu = styled.div`
    position: fixed;
    top: ${({ $top }) => $top}px;
    left: ${({ $left }) => $left}px;
    z-index: 1000;
    width: 128px;
    padding: 4px;
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    background-color: var(--color-White);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.14);
`;

const HeaderContextMenuItem = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    height: 28px;
    padding: 0 8px;
    border: 0;
    border-radius: 3px;
    background-color: transparent;
    color: ${({ $danger }) => ($danger ? "var(--color-Warn)" : "var(--color-SubBlack)")};
    font-size: 1.2rem;
    text-align: left;
    cursor: pointer;

    &:hover {
        background-color: var(--color-ListHover);
    }
`;

const BodyContainer = styled.div`
    position: relative;
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;
    flex: 1 1 0%;
    background-color: var(--color-White);
`;

const ListModeContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
`;

const ThreadListArea = styled.div`
    flex: 1;
    min-height: 0;
`;

const EmptySuggestionList = styled.div`
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px 16px;
    border-top: solid 1px #edf1f4;
    background-color: var(--color-White);
`;

const EmptySuggestionItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
`;

const EmptySuggestionTitle = styled(Text).attrs({
    variant: "body-sm-500",
    color: "var(--color-SubBlack)",
})`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const EmptySuggestionDescription = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

const EmptySuggestionButton = styled(Button).attrs({
    onlyText: true,
    width: "100%",
    height: "24px",
    bgColor: "var(--color-Button1)",
    hoverBgColor: "var(--color-ButtonHover1)",
    fontSize: "small",
    fontColor: "var(--color-White)",
})``;

const SlideFadeContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

function BodyContentContainer({ active, zIndex, enterFrom, leaveTo, children, ...props }) {
    const [shouldRender, setShouldRender] = useState(active);
    const [transitionState, setTransitionState] = useState({
        opacity: active ? 1 : 0,
        translate: "0%",
    });

    useEffect(() => {
        let timeout;

        // 입장 (active가 true)
        if (active) {
            setShouldRender(true);
            // 입장 시작점 세팅
            let fromX = "0%";
            if (enterFrom === "left") fromX = "100%";
            if (enterFrom === "right") fromX = "-100%";
            setTransitionState({ opacity: 0, translate: fromX });
            // 다음 tick에 트랜지션 시작
            setTimeout(() => {
                setTransitionState({ opacity: 1, translate: "0%" });
            }, 20); // 0~20ms 권장 (트랜지션 트리거)
        } else {
            // 퇴장 방향 세팅
            let toX = "0%";
            if (leaveTo === "left") toX = "-100%";
            if (leaveTo === "right") toX = "100%";
            setTransitionState({ opacity: 0, translate: toX });
            // 트랜지션 끝나면 unmount
            timeout = setTimeout(() => setShouldRender(false), 400);
        }
        return () => clearTimeout(timeout);
    }, [active, enterFrom, leaveTo]);

    if (!shouldRender) return null;

    return (
        <SlideFadeContainer
            style={{
                opacity: transitionState.opacity,
                transform: transitionState.translate === "0%" ? "none" : `translateX(${transitionState.translate})`,
                zIndex,
                transition: "opacity 400ms ease-in-out, transform 400ms ease-in-out",
            }}
            {...props}
        >
            {children}
        </SlideFadeContainer>
    );
}

const FooterContainer = styled.div`
    position: sticky;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    padding: 0 12px 12px;
    background-color: var(--color-White);
`;

const FooterChatBox = styled.div`
    margin: 8px 0 0 0;
    width: 100%;
    height: auto;
    background-color: var(--color-White);
    box-shadow: none;
`;

const FooterChatSelectedBoxContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    background-color: var(--color-White);
    border-top-right-radius: 5px;
    border-top-left-radius: 5px;
`;

const FooterChatSelectedBoxHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 10px;
    width: 100%;
    background-color: #f4f7f8;
`;

const FooterChatSelectedBoxTitle = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Black)",
})``;

function ReferenceDetailSidebar(props) {
    const { chatDelete, selectedThread, setSelectedThread, threadUpdateTitle } = props;
    const VIEW_THREAD_DETAIL = "thread_detail";
    const VIEW_THREAD_LIST = "thread_list";
    const currentView = props.selectedThread ? VIEW_THREAD_DETAIL : VIEW_THREAD_LIST;
    const inputMessagePlaceHolder =
        currentView === VIEW_THREAD_DETAIL ? "현재의 리뷰 스레드에 메시지 추가" : "새로운 리뷰 스레드 추가";

    const [inputMessage, setInputMessage] = useState("");
    const messageInputRef = useRef();
    const [headerActionMenu, setHeaderActionMenu] = useState(null);
    const [isEditingHeaderTitle, setIsEditingHeaderTitle] = useState(false);
    const [editingHeaderTitle, setEditingHeaderTitle] = useState("");
    const headerActionMenuRef = useRef(null);
    const skipHeaderTitleCommitRef = useRef(false);

    const handleInputMessage = (q) => {
        if (isMessageDocEmpty(q)) {
            return;
        }

        let req_message = {
            autoResponse: autoResponse,
            content: q,
            reviewSelectionId: props.selectedBox?.id || null,
            reviewSelectionColor: props.selectedBox?.id ? selectedBoxColor : null,
            newReviewSelectionData: props.selectedBox || null,
            parentMessageId: lastVisibleMessageId,
        };

        setInputMessage("");
        props.setSelectedBox && props.setSelectedBox(null);
        props.setSearchBox && props.setSearchBox(null);
        messageInputRef.current?.blur();

        props.chatStart(req_message);
    };

    const handleStartRecommendedChat = (content) => {
        if (!content) return;
        props.setSelectedBox && props.setSelectedBox(null);
        props.setSearchBox && props.setSearchBox(null);
        props.chatStart({
            autoResponse: true,
            content: stringifyMessageDoc(legacyTextToMessageDoc(content)),
            reviewSelectionId: null,
            reviewSelectionColor: null,
            newReviewSelectionData: null,
            parentMessageId: null,
        });
    };

    useEffect(() => {
        if (!headerActionMenu) return;

        const closeMenu = (event) => {
            if (headerActionMenuRef.current?.contains(event.target)) return;
            setHeaderActionMenu(null);
        };

        window.addEventListener("mousedown", closeMenu);
        return () => window.removeEventListener("mousedown", closeMenu);
    }, [headerActionMenu]);

    useEffect(() => {
        setHeaderActionMenu(null);
        setIsEditingHeaderTitle(false);
        setEditingHeaderTitle("");
    }, [props.selectedThread?.id]);

    const commitHeaderTitleEdit = useCallback(
        (titleValue = editingHeaderTitle) => {
            if (skipHeaderTitleCommitRef.current) {
                skipHeaderTitleCommitRef.current = false;
                return;
            }

            const nextTitle = titleValue.trim();
            const targetThread = selectedThread;
            setIsEditingHeaderTitle(false);
            setEditingHeaderTitle("");

            if (!targetThread || !nextTitle || isMessageDocEmpty(nextTitle) || nextTitle === targetThread.title) return;
            threadUpdateTitle?.(targetThread.id, nextTitle);
        },
        [editingHeaderTitle, selectedThread, threadUpdateTitle],
    );

    const handleDeleteSelectedThread = useCallback(() => {
        const targetThread = selectedThread;
        if (!targetThread?.id) return;

        setHeaderActionMenu(null);
        Confirm("warn", "리뷰 삭제", "리뷰를 삭제하면 되돌릴 수 없습니다. 삭제하시겠습니까?", "삭제", () => {
            chatDelete?.(targetThread.id, null, true);
            setSelectedThread(null);
        });
    }, [chatDelete, selectedThread, setSelectedThread]);

    // region: 스크롤, 포커스 관련 영역
    const bodyScrollRef = useRef();
    const [focusedMessageId, setFocusedMessageId] = useState(null);
    useEffect(() => {
        // 새로운 메시지 스트리밍 중 스크롤 처리
        if (props.autoScrollToEnd) {
            if (bodyScrollRef.current) {
                bodyScrollRef.current.scrollTop = bodyScrollRef.current.scrollHeight;
            }
        }
    }, [props.autoScrollToEnd, props.threads]);

    useLayoutEffect(() => {
        // 전환/레이아웃 플러시 후 최하단으로
        const raf1 = requestAnimationFrame(() => {
            const raf2 = requestAnimationFrame(() => {
                if (focusedMessageId) {
                    return;
                }
                if (bodyScrollRef.current) {
                    if (currentView === VIEW_THREAD_DETAIL) {
                        bodyScrollRef.current.scrollTop = bodyScrollRef.current.scrollHeight;
                    } else {
                        bodyScrollRef.current.scrollTop = 0;
                    }
                }
            });
            // cleanup inner
            return () => cancelAnimationFrame(raf2);
        });

        return () => cancelAnimationFrame(raf1);
    }, [currentView, focusedMessageId]);

    useEffect(() => {
        if (!focusedMessageId) return;
        const t = setTimeout(() => setFocusedMessageId(null), 3000);
        return () => clearTimeout(t);
    }, [focusedMessageId]);

    useEffect(() => {
        if (props.focusedMessageId) setFocusedMessageId(props.focusedMessageId);
    }, [props.focusedMessageId]);

    // selectedBox 의 color 설정
    const selectedBoxColor = props.selectedBox?.data?.color || "var(--color-HighlightRed)";

    useEffect(() => {
        // selectedBox가 바뀌면 입력창에 포커스
        if (props.selectedBox && messageInputRef.current) {
            messageInputRef.current.focus();
        }
    }, [props.selectedBox]);

    // options
    const [autoResponse, setAutoResponse] = useState(true);
    const hasPdfFile = !!props.reference?.file;
    const analysisStatus = props.pdfIndexingInfo?.analysis_status || Constants.FILE_ANALYSIS_TODO;
    const isAnalysisReady = hasPdfFile && analysisStatus === Constants.FILE_ANALYSIS_DONE;
    const disableRecommendedChat = !isAnalysisReady || isReadonlyUser(props.user);

    // region: 선택 스레드 메시지 랜더링

    // 선택 스레드 메시지 맵핑 생성
    const [messageMapByParent, setMessageMapByParent] = useState({});
    useEffect(() => {
        if (props.selectedThread) {
            const newMapByParent = {};
            props.selectedThread.messages.forEach((msg) => {
                const parent = msg.parent || null;
                if (!newMapByParent[parent]) {
                    newMapByParent[parent] = [];
                }
                newMapByParent[parent].push(msg);
            });
            setMessageMapByParent(newMapByParent);
        }
    }, [props.selectedThread]);

    // 선택 스레드 메시지 랜더(재귀)
    const [messageVersionMapByParent, setMessageVersionMapByParent] = useState({}); // 버전 선택 상태 관리 (부모ID별 선택된 버전)
    function handleChangeVersion(parentId, direction) {
        const children = messageMapByParent[parentId];
        if (!children || !children.length) return;

        const selectedVersion = messageVersionMapByParent[parentId] || children[children.length - 1].version;
        let targetVersion = selectedVersion + direction;
        if (targetVersion < 1) targetVersion = 1;
        if (targetVersion > children[children.length - 1].version)
            targetVersion = children[children.length - 1].version;
        setMessageVersionMapByParent((prev) => ({ ...prev, [parentId]: targetVersion }));
    }

    function renderSelectedThreadMessages({ parentId = null, renderMessage }) {
        const message = pickMessageByParentId(parentId);
        if (!message) return null;

        const messageComponent = renderMessage(message);

        if (message.is_deleted) {
            // 삭제된 메시지는 마지막이라고 보이더라도 하위는 렌더하지 않음
            return messageComponent;
        } else {
            return (
                <>
                    {messageComponent}
                    {renderSelectedThreadMessages({
                        parentId: message.id,
                        renderMessage,
                    })}
                </>
            );
        }
    }

    // 화면에 나타나는 마지막 메시지를 state 로 유지 (메시지 추가시 parent 로 활용)
    const [lastVisibleMessageId, setLastVisibleMessageId] = useState(null);

    // 부모 id 기준으로, 현재 버전 선택 상태를 반영한 메시지 하나를 리턴
    const pickMessageByParentId = useCallback(
        (parentId = null) => {
            const children = messageMapByParent[parentId];
            if (!children || children.length === 0) return null;

            // 기본은 마지막 child
            let message = children[children.length - 1];

            // 버전 선택되어 있으면 그 버전 우선
            if (messageVersionMapByParent[parentId]) {
                const v = messageVersionMapByParent[parentId];
                const found = children.find((m) => m.version === v);
                if (found) message = found;
            }
            return message;
        },
        [messageMapByParent, messageVersionMapByParent],
    );

    // 현재 selectedThread + version 선택을 기준으로, 논리적으로 마지막에 보이는 메시지의 id 계산
    const calcLastVisibleMessageId = useCallback(() => {
        let lastId = null;
        let parentId = null;

        while (true) {
            const message = pickMessageByParentId(parentId);
            if (!message) break;

            if (message.is_deleted) {
                // 마지막 메시지가 삭제된 경우, 바로 위(lastId)를 마지막으로 간주
                break;
            }

            lastId = message.id;
            parentId = message.id;
        }

        return lastId;
    }, [pickMessageByParentId]);
    useEffect(() => {
        if (!props.selectedThread) {
            setLastVisibleMessageId(null);
            return;
        }

        const message_id = calcLastVisibleMessageId();
        setLastVisibleMessageId(message_id);
    }, [calcLastVisibleMessageId, props.selectedThread]);

    // focusedMessageId 가 지정되면, 해당 메시지의 버전을 선택해 트리 구성
    // - focusedMessageId 자신이 속한 parent 에 대해, 그 parent 의 선택 버전을 해당 메시지의 version 으로 맞춘다.
    // - focusedMessageId 의 자식들에 대해서는 messageVersionMapByParent 를 건드리지 않으므로 기본(마지막 버전)으로 표시된다.
    useEffect(() => {
        if (!focusedMessageId || !props.selectedThread) return;

        // 현재 스레드에서 focusedMessageId 에 해당하는 메시지 찾기
        const targetMessage = props.selectedThread.messages.find((m) => m.id === focusedMessageId);
        if (!targetMessage) return;

        const parentId = targetMessage.parent || null;
        const children = messageMapByParent[parentId] || [];
        // parentId 아래의 children 중에서 실제로 동일한 메시지(id 기준)를 찾음
        const targetChild = children.find((m) => m.id === focusedMessageId);
        if (!targetChild) return;

        const targetVersion = targetChild.version;
        if (!targetVersion) return;

        setMessageVersionMapByParent((prev) => {
            // 이미 같은 버전이면 변경 없음
            if (prev[parentId] === targetVersion) return prev;
            return { ...prev, [parentId]: targetVersion };
        });
    }, [focusedMessageId, props.selectedThread, messageMapByParent]);

    return (
        <Container>
            {/* 사이드바 헤더 */}
            <Header>
                {currentView === VIEW_THREAD_DETAIL ? (
                    <HeaderMain>
                        <HeaderTitleGroup>
                            <Tooltip message="목록으로">
                                <HeaderButton type="button" onClick={() => props.setSelectedThread(null)}>
                                    <Icon name={"arrowLeft"} size={"14"} color={"var(--color-Grey1)"} />
                                </HeaderButton>
                            </Tooltip>
                            {isEditingHeaderTitle ? (
                                <HeaderTitleInput
                                    autoFocus
                                    value={editingHeaderTitle}
                                    onChange={setEditingHeaderTitle}
                                    onSubmit={commitHeaderTitleEdit}
                                    onCancel={() => {
                                        skipHeaderTitleCommitRef.current = true;
                                        setIsEditingHeaderTitle(false);
                                        setEditingHeaderTitle("");
                                    }}
                                    onBlur={() => commitHeaderTitleEdit()}
                                    placeholder="리뷰 제목"
                                    multiline={false}
                                    features={false}
                                    showSubmit={false}
                                />
                            ) : (
                                <HeaderTitle title={props.selectedThread?.title || "리뷰 상세"}>
                                    {props.selectedThread?.title ? (
                                        <MessageContent content={props.selectedThread.title} singleLine />
                                    ) : (
                                        "리뷰 상세"
                                    )}
                                </HeaderTitle>
                            )}
                        </HeaderTitleGroup>
                        <Tooltip message="더보기">
                            <HeaderActionButton
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    const rect = event.currentTarget.getBoundingClientRect();
                                    setHeaderActionMenu({
                                        top: rect.bottom + 4,
                                        left: rect.right - 128,
                                    });
                                }}
                            >
                                <Icon name="submenu" size="14" color="var(--color-Grey1)" />
                            </HeaderActionButton>
                        </Tooltip>
                    </HeaderMain>
                ) : currentView === VIEW_THREAD_LIST ? (
                    <HeaderMain>
                        <HeaderTitleGroup>
                            <HeaderTitle>리뷰</HeaderTitle>
                        </HeaderTitleGroup>
                    </HeaderMain>
                ) : null}
            </Header>
            {headerActionMenu && props.selectedThread && (
                <HeaderContextMenu ref={headerActionMenuRef} $top={headerActionMenu.top} $left={headerActionMenu.left}>
                    <HeaderContextMenuItem
                        type="button"
                        onClick={() => {
                            skipHeaderTitleCommitRef.current = false;
                            setIsEditingHeaderTitle(true);
                            setEditingHeaderTitle(props.selectedThread?.title || "");
                            setHeaderActionMenu(null);
                        }}
                    >
                        제목 수정
                    </HeaderContextMenuItem>
                    <HeaderContextMenuItem type="button" $danger onClick={handleDeleteSelectedThread}>
                        리뷰 삭제
                    </HeaderContextMenuItem>
                </HeaderContextMenu>
            )}

            <BodyContainer ref={bodyScrollRef}>
                {/* 스레드 상세 */}
                <BodyContentContainer
                    active={currentView === VIEW_THREAD_DETAIL}
                    zIndex={2}
                    enterFrom={"left"}
                    leaveTo={"right"}
                >
                    {props.selectedThread &&
                        renderSelectedThreadMessages({
                            parentId: null,
                            renderMessage: (message) => (
                                <ReferenceReviewMessage
                                    key={message.id}
                                    thread={props.selectedThread}
                                    message={message}
                                    profiles={props.profiles}
                                    myProfileId={props.myProfileId}
                                    reference={props.reference}
                                    setCurPage={props.setCurPage}
                                    setSearchBox={props.setSearchBox}
                                    focused={focusedMessageId === message.id}
                                    streamingMessageIds={props.streamingMessageIds}
                                    autoResponse={autoResponse}
                                    chatStart={props.chatStart}
                                    chatDelete={props.chatDelete}
                                    versionCount={(() => {
                                        const siblings = messageMapByParent[message.parent] || [];
                                        return siblings.length > 0 ? siblings.length : 1;
                                    })()}
                                    onChangeVersion={(direction) =>
                                        handleChangeVersion(message.parent || null, direction)
                                    }
                                />
                            ),
                        })}
                </BodyContentContainer>

                <BodyContentContainer active={currentView === VIEW_THREAD_LIST} zIndex={2}>
                    <ListModeContainer>
                        <ThreadListArea>
                            <ReferenceReviewThreadList
                                threads={props.threads}
                                isLoading={props.isThreadListLoading}
                                selectedThread={props.selectedThread}
                                onSelectThread={props.setSelectedThread}
                                threadUpdateTitle={props.threadUpdateTitle}
                                chatDelete={props.chatDelete}
                                showHeader={false}
                                exposeLegacyTitleClass={false}
                            />
                        </ThreadListArea>
                        {hasPdfFile && (
                            <EmptySuggestionList>
                                <EmptySuggestionItem>
                                    <EmptySuggestionTitle>
                                        <Icon name="ai" size="14" color="var(--color-Grey1)" />
                                        논문 요약
                                    </EmptySuggestionTitle>
                                    <EmptySuggestionDescription>
                                        연구 주제, 연구 방법, 연구 결과를 간결하게 요약하여 리뷰 메시지를 생성합니다.
                                    </EmptySuggestionDescription>
                                    <EmptySuggestionButton
                                        buttonText="분석 시작"
                                        onClick={() =>
                                            handleStartRecommendedChat(
                                                "이 논문의 전체 페이지를 확인한 후, 이 논문의 연구 주제, 연구 방법, 연구 결과에 대해 쉽고 간결하게 요약해줘.",
                                            )
                                        }
                                        disabled={disableRecommendedChat}
                                    />
                                </EmptySuggestionItem>
                                <EmptySuggestionItem>
                                    <EmptySuggestionTitle>
                                        <Icon name="ai" size="14" color="var(--color-Grey1)" />
                                        논문 비평
                                    </EmptySuggestionTitle>
                                    <EmptySuggestionDescription>
                                        논문의 훌륭한 점과 아쉬운 점, 보완 방향을 제시하는 리뷰 메시지를 생성합니다.
                                    </EmptySuggestionDescription>
                                    <EmptySuggestionButton
                                        buttonText="분석 시작"
                                        onClick={() =>
                                            handleStartRecommendedChat(
                                                "이 논문의 전체 페이지를 확인한 후, 전문가의 관점으로 이 논문의 훌륭한 점, 아쉬운 점, 보완 방향을 제시하고 종합적인 평가를 내려줘.",
                                            )
                                        }
                                        disabled={disableRecommendedChat}
                                    />
                                </EmptySuggestionItem>
                            </EmptySuggestionList>
                        )}
                    </ListModeContainer>
                </BodyContentContainer>
            </BodyContainer>
            <FooterContainer>
                <FooterChatBox
                    onClick={(e) => {
                        // 푸터 부분 클릭했을 때, Container 의 onClick 방지
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <FooterChatSelectedBoxContainer>
                        <FooterChatSelectedBoxHeader>
                            <FooterChatSelectedBoxTitle>
                                {currentView === VIEW_THREAD_DETAIL ? "새로운 메시지" : "새로운 리뷰"}
                            </FooterChatSelectedBoxTitle>
                        </FooterChatSelectedBoxHeader>
                    </FooterChatSelectedBoxContainer>
                    <MessageInput
                        ref={messageInputRef}
                        autoFocus
                        value={inputMessage}
                        onChange={setInputMessage}
                        onSubmit={handleInputMessage}
                        placeholder={inputMessagePlaceHolder}
                        mentions={{ enabled: true, data: mentionCandidates }}
                        submitDisabled={isMessageDocEmpty(inputMessage) || isReadonlyUser(props?.user)}
                        stopAction={
                            props.streamingMessageIds.length > 0
                                ? {
                                      key: "stop",
                                      icon: "x",
                                      tooltip: "응답 중지",
                                      onClick: props.chatStop,
                                  }
                                : null
                        }
                        bottomSlot={
                            <Tooltip message={"메시지에 @PREP 멘션을 자동으로 추가합니다."}>
                                <Checkbox
                                    label={"AI 자동 응답"}
                                    size={"regular"}
                                    onChange={() => setAutoResponse(!autoResponse)}
                                    checked={autoResponse}
                                />
                            </Tooltip>
                        }
                    />
                </FooterChatBox>
            </FooterContainer>
        </Container>
    );
}

export default ReferenceDetailSidebar;
